import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import ReceiptTwoToneIcon from "@mui/icons-material/ReceiptTwoTone";
import {
	Avatar,
	Box,
	Chip,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useTheme
} from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { imgUrl } from "src/Helpers/utils/constants";
import type { ISessionEnroll } from "src/Types/module/b2bSession/b2bSessionEnroll.types";
import UpdateSessionEnrollModal from "./UpdateSessionEnrollModal";

interface Props {
	enroll: ISessionEnroll;
	serial?: number;
	hideMember?: boolean;
}

const formatDisplayTime = (timeStr?: string | null) => {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	if (hours === undefined || minutes === undefined) return timeStr;
	const dummyDate = dayjs().hour(Number(hours)).minute(Number(minutes));
	return dummyDate.format("hh:mm A");
};

const getStatusChip = (status?: string) => {
	const normalized = (status || "pending").toLowerCase();

	let bg = "rgba(245, 158, 11, 0.12)";
	let color = "#D97706";
	let border = "1px solid rgba(245, 158, 11, 0.3)";
	let label = "Pending";

	if (normalized === "approved") {
		bg = "rgba(16, 185, 129, 0.12)";
		color = "#059669";
		border = "1px solid rgba(16, 185, 129, 0.3)";
		label = "Approved";
	} else if (normalized === "rejected") {
		bg = "rgba(239, 68, 68, 0.12)";
		color = "#DC2626";
		border = "1px solid rgba(239, 68, 68, 0.3)";
		label = "Rejected";
	} else if (normalized === "completed") {
		bg = "rgba(14, 165, 233, 0.12)";
		color = "#0284C7";
		border = "1px solid rgba(14, 165, 233, 0.3)";
		label = "Completed";
	}

	return (
		<Chip
			label={label}
			size="small"
			sx={{
				bgcolor: bg,
				color: color,
				border: border,
				fontWeight: 700,
				fontSize: "0.75rem",
				textTransform: "capitalize",
			}}
		/>
	);
};

const SessionEnrollTableRow: React.FC<Props> = ({
	enroll,
	serial,
	hideMember = false,
}) => {
	const theme = useTheme();
	const [openEdit, setOpenEdit] = useState(false);

	const fullName = `${enroll.b2b_first_name || ""} ${enroll.b2b_last_name || ""
		}`.trim();

	const startTimeFormatted = formatDisplayTime(enroll.start_time);
	const endTimeFormatted = formatDisplayTime(enroll.end_time);

	return (
		<>
			<TableRow hover>
				{/* SL. */}
				<TableCell>{serial ?? enroll.id}</TableCell>

				{/* B2B Details */}
				{!hideMember && (
					<TableCell>
						{fullName || enroll.b2b_organization || enroll.b2b_phone || enroll.b2b_email ? (
							<Box display="flex" alignItems="center" gap={1.5}>
								<Avatar
									src={
										enroll.b2b_photo
											? `${imgUrl}/${enroll.b2b_photo}`
											: undefined
									}
									alt={fullName}
									sx={{
										width: 38,
										height: 38,
										fontSize: "0.875rem",
										bgcolor: "primary.main",
										fontWeight: 700,
									}}
								>
									{fullName ? fullName.charAt(0).toUpperCase() : "B"}
								</Avatar>
								<Box minWidth={0}>
									{enroll.b2b_id ? (
										<Typography
											component={RouterLink}
											to={`/b2b/b2b-view/${enroll.b2b_id}`}
											variant="subtitle2"
											fontWeight={700}
											sx={{
												color: "primary.main",
												textDecoration: "none",
												"&:hover": { textDecoration: "underline" },
												display: "block",
											}}
										>
											{fullName || `B2B Member #${enroll.b2b_id}`}
										</Typography>
									) : (
										<Typography variant="subtitle2" fontWeight={700}>
											{fullName || "N/A"}
										</Typography>
									)}
									{enroll.b2b_organization && (
										<Typography
											variant="body1"
											color="text.secondary"
											fontWeight={600}
											display="block"
										>
											{enroll.b2b_organization}
										</Typography>
									)}
									<Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center" mt={0.25}>
										{enroll.b2b_phone && (
											<Typography variant="caption" color="text.secondary">
												{enroll.b2b_phone}
											</Typography>
										)}
										{enroll.b2b_phone && enroll.b2b_email && (
											<Typography variant="caption" color="text.disabled">
												•
											</Typography>
										)}
										{enroll.b2b_email && (
											<Typography variant="body1" color="text.secondary">
												{enroll.b2b_email}
											</Typography>
										)}
									</Box>
								</Box>
							</Box>
						) : (
							<Typography variant="body2" color="text.disabled">
								N/A
							</Typography>
						)}
					</TableCell>
				)}

				{/* Session Slot */}
				<TableCell>
					<Typography variant="body2" fontWeight={600}>
						{enroll.session_no || `N/A`}
					</Typography>
					<Typography variant="caption" color="text.secondary" display="block">
						{enroll.date ? dayjs(enroll.date).format("DD MMM, YYYY") : "—"}
						{startTimeFormatted && endTimeFormatted
							? ` (${startTimeFormatted} – ${endTimeFormatted})`
							: ""}
					</Typography>
				</TableCell>

				{/* Invoice Info */}
				<TableCell>
					{enroll.invoice_no ? (
						<Box>
							<Box display="flex" alignItems="center" gap={0.5}>
								<ReceiptTwoToneIcon sx={{ fontSize: 16, color: "info.main" }} />
								{enroll.invoice_id ? (
									<Typography
										component={RouterLink}
										to={`/b2b/b2b-invoice/${enroll.invoice_id}`}
										variant="body2"
										fontWeight={600}
										sx={{
											color: "info.main",
											textDecoration: "none",
											"&:hover": { textDecoration: "underline" },
										}}
									>
										{enroll.invoice_no}
									</Typography>
								) : (
									<Typography variant="body2" fontWeight={600}>
										{enroll.invoice_no}
									</Typography>
								)}
							</Box>
							<Box display="flex" gap={1} mt={0.25}>
								{enroll.net_total && (
									<Typography variant="caption" color="text.secondary">
										Total: ৳{enroll.net_total}
									</Typography>
								)}
								{enroll.invoice_due && (
									<Typography
										variant="caption"
										sx={{
											color:
												Number(enroll.invoice_due) > 0
													? "error.main"
													: "success.main",
											fontWeight: 600,
										}}
									>
										Due: ৳{enroll.invoice_due}
									</Typography>
								)}
							</Box>
						</Box>
					) : (
						<Typography variant="caption" color="text.disabled">
							No Invoice
						</Typography>
					)}
				</TableCell>

				{/* Status */}
				<TableCell>{getStatusChip(enroll.status)}</TableCell>

				{/* Created At */}
				<TableCell>
					<Typography variant="body2" color="text.secondary">
						{enroll.created_at
							? dayjs(enroll.created_at).format("DD MMM, YYYY")
							: "—"}
					</Typography>
					<Typography variant="caption" color="text.disabled" display="block">
						{enroll.created_at
							? dayjs(enroll.created_at).format("hh:mm A")
							: ""}
					</Typography>
				</TableCell>

				{/* Actions */}
				<TableCell align="right">
					<Box
						display="flex"
						alignItems="center"
						justifyContent="flex-end"
						gap={0.5}
					>
						<Tooltip title="Edit Enrollment" arrow>
							<IconButton
								onClick={() => setOpenEdit(true)}
								size="small"
								sx={{
									color: theme.palette.success.dark,
									"&:hover": {
										bgcolor: theme.colors.success.lighter,
									},
								}}
							>
								<EditTwoToneIcon fontSize="small" />
							</IconButton>
						</Tooltip>


					</Box>
				</TableCell>
			</TableRow>

			{/* Edit Modal */}
			{openEdit && (
				<UpdateSessionEnrollModal
					open={openEdit}
					handleClose={() => setOpenEdit(false)}
					enroll={enroll}
				/>
			)}
		</>
	);
};

export default SessionEnrollTableRow;
